import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MemberAuthorityRole, MemberListItem } from '@m8-app/contracts/rest/members.js';
import {
  getAdminMembers,
  adminCreateMember,
  adminDeleteMember,
  adminAssignDevicesToMember,
} from '../api/admin.js';
import type { AdminCreateMemberBody } from '@m8-app/contracts/rest/admin.js';

const AUTHORITY_ROLES: MemberAuthorityRole[] = [
  'main_user',
  'sub_main_user',
  'member',
  'sub_member',
];

export function Members() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberListItem | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['members'],
    queryFn: getAdminMembers,
  });

  const addMutation = useMutation({
    mutationFn: adminCreateMember,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['members'] }); setShowAdd(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteMember,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members'] }),
  });

  if (isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (isError) return <div className="p-8 text-sm text-red-600">Failed to load members. Please refresh.</div>;

  const members = data?.members ?? [];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Members</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
        >
          + Add Member
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Mobile</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {members.map((m) => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">
                  <button
                    onClick={() => setSelectedMember(m)}
                    className="hover:text-indigo-600 text-left"
                  >
                    {m.name}
                  </button>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.mobile}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700">
                    {m.authorityRole.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => {
                      if (confirm(`Remove member ${m.name}?`)) deleteMutation.mutate(m.id);
                    }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  No members yet. Add one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onSubmit={(body) => addMutation.mutate(body)}
          isPending={addMutation.isPending}
          {...(addMutation.error?.message !== undefined && { error: addMutation.error.message })}
        />
      )}

      {selectedMember && (
        <MemberDrawer
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </div>
  );
}

// ── Add Member Modal ───────────────────────────────────────────────────────

function AddMemberModal({
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  onClose: () => void;
  onSubmit: (body: AdminCreateMemberBody) => void;
  isPending: boolean;
  error?: string;
}) {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState<MemberAuthorityRole>('member');
  const [parentUserId, setParentUserId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, mobile, authorityRole: role, parentUserId });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Member</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="member-parent" className="block text-sm font-medium text-gray-700 mb-1">Parent User ID</label>
            <input
              id="member-parent"
              type="text"
              value={parentUserId}
              onChange={(e) => setParentUserId(e.target.value)}
              placeholder="UUID of the user who owns this member"
              required
              pattern="^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="member-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              id="member-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="member-mobile" className="block text-sm font-medium text-gray-700 mb-1">Mobile (E.164)</label>
            <input
              id="member-mobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+14155551234"
              required
              pattern="^\+\d{10,15}$"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="member-role" className="block text-sm font-medium text-gray-700 mb-1">Authority Role</label>
            <select
              id="member-role"
              value={role}
              onChange={(e) => setRole(e.target.value as MemberAuthorityRole)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {AUTHORITY_ROLES.map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Member Drawer (device assignment) ─────────────────────────────────────

function MemberDrawer({ member, onClose }: { member: MemberListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [deviceId, setDeviceId] = useState('');

  const assignMutation = useMutation({
    mutationFn: (deviceIds: string[]) => adminAssignDevicesToMember(member.id, { deviceIds }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members'] }),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
            <p className="text-sm text-gray-500 font-mono">{member.mobile}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Assign Device</h4>
          <div className="flex gap-2">
            <input
              type="text"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              placeholder="Device UUID"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={() => {
                if (deviceId.trim()) assignMutation.mutate([deviceId.trim()]);
              }}
              disabled={assignMutation.isPending || !deviceId.trim()}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {assignMutation.isPending ? '…' : 'Assign'}
            </button>
          </div>
          {assignMutation.isSuccess && (
            <p className="text-sm text-green-600 mt-2">Device assigned.</p>
          )}
          {assignMutation.isError && (
            <p className="text-sm text-red-600 mt-2">{assignMutation.error?.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}
